import {
  DocumentTextIcon,
  CheckIcon,
  XCircleIcon,
  PencilIcon,
  InformationCircleIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";

const RoomCleaningConfig = ({
  roomCleaning,
  isEditMode,
  cleaningError,
  onToggleEditMode,
  onSaveConfig,
  onCleaningUpdate,
  cleaningOptions,
  canEdit = true,
}) => {
  const getBadgeColor = (type) => {
    const colors = {
      Regular: "bg-blue-100 text-blue-800",
      Allergen: "bg-yellow-100 text-yellow-800",
      Enhanced: "bg-purple-100 text-purple-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const getDescription = (type) => {
    const descriptions = {
      Regular: "Standard Cleaning",
      Allergen: "Allergen Control",
      Enhanced: "Deep Cleaning & Sanitization",
    };
    return descriptions[type] || type;
  };

  const getTimeColor = (type) => {
    const colors = {
      Regular: "text-blue-600",
      Allergen: "text-yellow-600",
      Enhanced: "text-purple-600",
    };
    return colors[type] || "text-gray-600";
  };

  const handleEnterEditMode = () => {
    if (canEdit) {
      onToggleEditMode();
    }
  };

  return (
    <div className="mb-8 bg-white p-6 rounded-xl shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-start gap-3">
          <DocumentTextIcon className="w-5 h-5 text-blue-500 mt-1.5 flex-shrink-0" />
          <div>
            <h4 className="text-lg font-semibold text-gray-900">
              Room Cleaning Configuration
            </h4>
            <p className="text-sm text-gray-500 mt-1">
              Configure cleaning times (minutes) for each room type and
              protocol. {cleaningError && "(Using default times)"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isEditMode ? (
            <>
              <button
                onClick={onSaveConfig}
                disabled={!canEdit}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm flex items-center gap-2 ${
                  canEdit
                    ? "bg-black text-white hover:bg-gray-900"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
                title={
                  !canEdit
                    ? "You do not have permission to save changes"
                    : undefined
                }
              >
                <CheckIcon className="w-4 h-4" />
                Save Changes
              </button>
              <button
                onClick={onToggleEditMode}
                className="px-4 py-2 bg-[#cf350d] text-white rounded-lg text-sm font-medium hover:bg-[#b42e0c] transition-all duration-200 flex items-center gap-2"
              >
                <XCircleIcon className="w-4 h-4" />
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={handleEnterEditMode}
              disabled={!canEdit}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm flex items-center gap-2 ${
                canEdit
                  ? "bg-black text-white hover:bg-gray-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
              title={
                !canEdit
                  ? "You do not have permission to edit cleaning settings"
                  : "Edit cleaning times"
              }
            >
              {canEdit ? (
                <PencilIcon className="w-4 h-4" />
              ) : (
                <LockClosedIcon className="w-4 h-4" />
              )}
              {canEdit ? "Edit Cleaning Times" : "Edit Locked"}
            </button>
          )}
        </div>
      </div>

      {/* Info notice */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <InformationCircleIcon className="w-4 h-4 flex-shrink-0" />
          <span>
            Cleaning times represent estimated duration in minutes for each
            protocol
          </span>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block overflow-x-auto">
        <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200 min-w-max">
          <table className="w-full border-collapse min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <th className="py-4 px-4 text-left font-semibold text-gray-900 border-b border-gray-200 sticky left-0 bg-gradient-to-r from-blue-50 to-indigo-50 z-10">
                  <div className="flex items-center gap-2 min-w-[160px]">
                    <DocumentTextIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span>Cleaning Protocol</span>
                  </div>
                </th>

                {roomCleaning.map((roomConfig) => (
                  <th
                    key={roomConfig._id || roomConfig.room}
                    className="py-4 px-4 text-center font-semibold text-gray-900 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 min-w-[120px] max-w-[160px]"
                  >
                    <div className="flex flex-col items-center">
                      <span className="text-sm font-bold">
                        {roomConfig.room}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {cleaningOptions.map((cleanType, typeIndex) => (
                <tr
                  key={cleanType}
                  className={`hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                    typeIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  <td className="py-4 px-4 font-semibold text-gray-900 border-b border-gray-200 sticky left-0 bg-gray-50 z-10">
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getBadgeColor(
                          cleanType
                        )}`}
                      >
                        {cleanType}
                      </span>

                      <span className="text-sm text-gray-600 truncate">
                        {getDescription(cleanType)}
                      </span>
                    </div>
                  </td>

                  {roomCleaning.map((roomConfig, roomIndex) => {
                    const currentTime =
                      roomConfig.cleaning[cleanType.toLowerCase()] || 0;

                    return (
                      <td
                        key={`${
                          roomConfig._id || roomConfig.room
                        }-${cleanType}`}
                        className="py-4 px-4 border-b border-gray-200 text-center"
                      >
                        {isEditMode ? (
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="number"
                              value={currentTime}
                              onChange={(e) =>
                                onCleaningUpdate(
                                  roomIndex,
                                  cleanType,
                                  e.target.value
                                )
                              }
                              className="w-20 px-3 py-2 text-center border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm font-medium"
                              min="1"
                            />
                            <span className="text-xs text-gray-500">
                              minutes
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`text-lg font-bold ${getTimeColor(
                                cleanType
                              )}`}
                            >
                              {currentTime}
                            </span>
                            <span className="text-xs text-gray-500">
                              minutes
                            </span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="sm:hidden space-y-4">
        {cleaningOptions.map((cleanType) => {
          const description = getDescription(cleanType);
          return (
            <div
              key={cleanType}
              className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getBadgeColor(
                      cleanType
                    )}`}
                  >
                    {cleanType}
                  </span>
                  <span className="text-sm text-gray-600">{description}</span>
                </div>
                <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                  minutes
                </span>
              </div>
              <div className="space-y-3">
                {roomCleaning.map((roomConfig, roomIndex) => {
                  const currentTime =
                    roomConfig.cleaning[cleanType.toLowerCase()] || 0;
                  return (
                    <div
                      key={roomConfig._id || roomConfig.room}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm font-medium text-gray-900">
                        {roomConfig.room}
                      </span>
                      <div className="text-right">
                        {isEditMode ? (
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="number"
                              value={currentTime}
                              onChange={(e) =>
                                onCleaningUpdate(
                                  roomIndex,
                                  cleanType,
                                  e.target.value
                                )
                              }
                              disabled={!canEdit}
                              className={`w-16 px-2 py-1 text-center border-2 rounded-lg text-sm font-medium transition-all ${
                                canEdit
                                  ? "border-gray-200 focus:ring-2 focus:ring-blue-500"
                                  : "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                              }`}
                              min="1"
                            />
                            <span className="text-xs text-gray-500">min</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`text-sm font-bold ${getTimeColor(
                                cleanType
                              )}`}
                            >
                              {currentTime}
                            </span>
                            <span className="text-xs text-gray-500">min</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit Mode Notice */}
      {isEditMode && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3 text-sm text-blue-800">
            <InformationCircleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p>
                Click <strong>Save</strong> to apply changes or{" "}
                <strong>Cancel</strong> to discard.
              </p>
              <p className="mt-1 text-blue-700 text-xs">
                Enter estimated cleaning time in minutes for each protocol.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomCleaningConfig;
